import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import CourseSelect from "./CourseSelect";
import Scorecard from "./Scorecard";
import Landing from "./Landing";
import Players from "./Players";
import History from "./History";
import {
  playerCourseSearch,
  playerLoadFacets,
  cacheFavoriteCourse,
  resolveFavoriteCourses,
} from "./catalogSource";
import { Course, Player, ScoreMap } from "./types";
import {
  ActiveGame,
  ActiveRound,
  BingoSelections,
  WolfSelections,
  appendHistory,
  clearActiveRound,
  createActiveRound,
  loadActiveRound,
  saveActiveRound,
} from "./activeRound";
import {
  hydrateFromBackend,
  loadFavorites,
  toggleFavorite,
  MAX_FAVORITES,
} from "./caddieGamesStore";
import {
  PlayerTournament,
  buildRoundFromTournament,
  claimScorer,
  loadMyTournaments,
  submitGroupScores,
  todayLocalISO,
  formatTournamentDate,
} from "./tournamentPlay";
import TournamentDrawModal from "./modals/TournamentDrawModal";
import { connectedTargetsForNames } from "./roster";
import { shareRound } from "./connections";
import { useAuthentication } from "../../context/AuthContext";
import "./caddiegames.css";

type View = "landing" | "select-course" | "scorecard" | "players" | "history";

const PERSIST_DEBOUNCE_MS = 300;

export default function CaddieGames() {
  const [view, setView] = useState<View>("landing");
  const [activeRound, setActiveRound] = useState<ActiveRound | null>(null);
  // Skip the persist effect until the initial hydrate has run, otherwise
  // the first render writes a null active round on top of the persisted
  // one before the load completes.
  const hydratedRef = useRef(false);

  // Header title click reset. Sub-views in this module are tracked by
  // local `view` state, not by the URL — Header's title <Link> always
  // resolves to "/caddiegames" for users whose default landing is this
  // module, so clicking it from inside (e.g. the Players screen) leaves
  // the URL unchanged and the local `view` survives. react-router v6
  // mints a new `location.key` on every navigate (including same-path
  // navigates), so observing key changes lets us snap back to the
  // landing screen without Header needing any module-specific logic.
  // Initial mount is a no-op since `view` already starts at "landing".
  const location = useLocation();
  useEffect(() => {
    setView("landing");
  }, [location.key]);

  const [hydrated, setHydrated] = useState(false);
  // Course catalog base layer. Starts from the cached/bundled seed for an
  // instant list, then refreshes from the shared backend catalog (admin edits)
  // when online. CourseSelect layers the player's own local edits on top.
  // Course catalog is fetched on demand by CourseSelect (server mode); the app
  // no longer holds the whole list. Favourites (ids) are resolved to course
  // objects for the quick-pick strip.
  const [favorites, setFavorites] = useState<string[]>(loadFavorites);
  const [favoriteCourses, setFavoriteCourses] = useState<Course[]>([]);
  const [favMsg, setFavMsg] = useState<string | null>(null);

  // Resolve favourite ids → course objects whenever the id list changes.
  useEffect(() => {
    let alive = true;
    resolveFavoriteCourses(favorites).then((cs) => {
      if (alive) setFavoriteCourses(cs);
    });
    return () => {
      alive = false;
    };
  }, [favorites]);

  const handleToggleFavorite = (course: Course) => {
    const { favorites: next, blocked } = toggleFavorite(course.id);
    if (blocked) {
      setFavMsg(
        `You can favourite up to ${MAX_FAVORITES} courses — remove one to add another.`,
      );
      return;
    }
    cacheFavoriteCourse(course);
    setFavMsg(null);
    setFavorites(next);
  };
  // Auto-dismiss the cap notice.
  useEffect(() => {
    if (!favMsg) return;
    const t = setTimeout(() => setFavMsg(null), 4000);
    return () => clearTimeout(t);
  }, [favMsg]);
  // Tournaments the player is in (open/live, member groups). Loaded after
  // hydrate; surfaced on the course-select screen for today's date.
  const [myTournaments, setMyTournaments] = useState<PlayerTournament[]>([]);
  // A future-dated tournament the player tapped to preview its draw (read-only).
  const [drawTournament, setDrawTournament] = useState<PlayerTournament | null>(
    null,
  );
  const { user } = useAuthentication();
  const userID: string | undefined = user?.userID;

  // Hydrate once we know who's logged in. Pulls the user's blob from
  // Cosmos and writes it into the localStorage keys roster.ts /
  // activeRound.ts read, THEN reads the active round into component
  // state. The render below is gated on `hydrated` so consumer pages
  // (Players, History) don't read empty localStorage before the fetch
  // completes — otherwise a logged-in user on a fresh install would see
  // an empty roster for a beat before it pops in.
  //
  // We wait for `userID` (the Auth0 sub) and pass it in so hydrate can
  // detect an account switch on this device and wipe the previous user's
  // cache before merging — without it, a second account logging in on the
  // same browser would inherit the first account's roster / history /
  // in-progress round. The didHydrateRef guard keeps this to a single run
  // even though the effect now keys off userID.
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (didHydrateRef.current) return;
    if (!userID) return; // wait until the session resolves the user id
    didHydrateRef.current = true;
    void (async () => {
      await hydrateFromBackend(userID);
      const stored = loadActiveRound();
      if (stored) setActiveRound(stored);
      // Favourites may have arrived from another device in the hydrate merge.
      setFavorites(loadFavorites());
      hydratedRef.current = true;
      setHydrated(true);
      // Tournaments come from the backend; load after hydrate so the
      // course-select screen can surface today's. Non-blocking.
      setMyTournaments(await loadMyTournaments());
    })();
  }, [userID]);

  // Persist on change (debounced) — keeps typing in score / name fields
  // off the disk on every keystroke.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!activeRound) {
      clearActiveRound();
      return;
    }
    const handle = setTimeout(() => {
      saveActiveRound(activeRound);
    }, PERSIST_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [activeRound]);

  // The live tournament this round belongs to (if still open/loaded). Used to
  // read the admin's current games config AND to backfill the group id/name for
  // rounds started before those fields were stored on the round.
  const liveTournament = activeRound?.tournamentId
    ? myTournaments.find((t) => t.tournamentId === activeRound.tournamentId)
    : undefined;
  const activeGroupId =
    liveTournament?.groupId ?? activeRound?.tournamentGroupId;
  const activeGroupName =
    liveTournament?.groupName ?? activeRound?.tournamentGroupName;

  // Tournament rounds: push this group's scores to the backend (debounced) so
  // they merge into the cross-group leaderboard for everyone. The server only
  // accepts the group's scorer, so non-scorers are silently no-ops.
  useEffect(() => {
    if (!hydratedRef.current) return;
    const r = activeRound;
    if (!r?.tournamentId || !activeGroupId) return;
    const handle = setTimeout(() => {
      void submitGroupScores(
        r.tournamentId!,
        activeGroupId,
        activeGroupName ?? "",
        r.players,
        r.scores,
      );
    }, 1000);
    return () => clearTimeout(handle);
  }, [activeRound, activeGroupId, activeGroupName]);

  // ---------- transitions ----------

  const startNewRound = (course: Course) => {
    setActiveRound(createActiveRound(course));
    setView("scorecard");
  };

  const resumeRound = () => {
    if (activeRound) setView("scorecard");
  };

  // Tap a tournament → claim the group scorer slot (first-claim-wins, server
  // side) and drop straight into a pre-seeded round (course + group players +
  // assigned tees + tournament games). Bypasses course/player selection.
  const startTournamentRound = (t: PlayerTournament) => {
    setActiveRound(buildRoundFromTournament(t));
    void claimScorer(t.tournamentId, t.groupId);
    setView("scorecard");
  };

  const endRound = () => {
    if (!activeRound) return;
    const entry = {
      ...activeRound,
      endedAt: new Date().toISOString(),
    };
    appendHistory(entry);
    // Fan the finished round out to any connected participants so it appears
    // in their history automatically — no separate share step.
    const targets = connectedTargetsForNames(entry.players.map((p) => p.name));
    if (targets.length) void shareRound(entry, targets);
    // Tournament round: mark this group's card FINAL (submitted). The server
    // makes final sticky and the admin sees the "submitted ✓" indicator;
    // once final the tournament drops from this player's Play list, so we
    // refresh it after the submit lands.
    if (entry.tournamentId && activeGroupId) {
      void (async () => {
        await submitGroupScores(
          entry.tournamentId!,
          activeGroupId,
          activeGroupName ?? "",
          entry.players,
          entry.scores,
          true,
        );
        setMyTournaments(await loadMyTournaments());
      })();
    }
    setActiveRound(null);
    setView("landing");
  };

  // ---------- per-field setters wired into Scorecard ----------

  // Each setter mirrors React.Dispatch<SetStateAction<...>> so the existing
  // functional-update calls inside Scorecard (e.g. setScores(prev => ...))
  // continue to work without changes.

  const setPlayers: React.Dispatch<React.SetStateAction<Player[]>> = (next) => {
    setActiveRound((round) => {
      if (!round) return round;
      const players =
        typeof next === "function"
          ? (next as (prev: Player[]) => Player[])(round.players)
          : next;
      return {
        ...round,
        players,
        lastTouchedAt: new Date().toISOString(),
      };
    });
  };

  const setScores: React.Dispatch<React.SetStateAction<ScoreMap>> = (next) => {
    setActiveRound((round) => {
      if (!round) return round;
      const scores =
        typeof next === "function"
          ? (next as (prev: ScoreMap) => ScoreMap)(round.scores)
          : next;
      return {
        ...round,
        scores,
        lastTouchedAt: new Date().toISOString(),
      };
    });
  };

  const setDisplayTeeId = (id: string | undefined) => {
    setActiveRound((round) =>
      round
        ? {
            ...round,
            displayTeeId: id,
            lastTouchedAt: new Date().toISOString(),
          }
        : round,
    );
  };

  // Games config travels alongside players/scores in the active round.
  // The Games modal hands us a fully-replacing array on every change,
  // so the proxy is a straight setter (no functional-update form needed).
  const setGames = (next: ActiveGame[]) => {
    setActiveRound((round) =>
      round
        ? {
            ...round,
            games: next,
            lastTouchedAt: new Date().toISOString(),
          }
        : round,
    );
  };

  // Per-hole Oosler/Woosler selections. Set by the Oosler trigger
  // modal in Scorecard — same lifecycle as scores so the selection
  // survives navigation and rehydrates with the round.
  const setOoslerSelections = (
    next: Record<number, string | null>,
  ) => {
    setActiveRound((round) =>
      round
        ? {
            ...round,
            ooslerSelections: next,
            lastTouchedAt: new Date().toISOString(),
          }
        : round,
    );
  };

  // Per-hole Wolf decisions — same lifecycle as ooslerSelections.
  const setWolfSelections = (next: WolfSelections) => {
    setActiveRound((round) =>
      round
        ? {
            ...round,
            wolfSelections: next,
            lastTouchedAt: new Date().toISOString(),
          }
        : round,
    );
  };

  // Per-hole Bingo Bango Bongo winners — same lifecycle.
  const setBingoSelections = (next: BingoSelections) => {
    setActiveRound((round) =>
      round
        ? {
            ...round,
            bingoSelections: next,
            lastTouchedAt: new Date().toISOString(),
          }
        : round,
    );
  };

  // ---------- render ----------

  // Block render until the backend blob has been written to localStorage.
  // Otherwise Players / History / the resume-round indicator can read
  // empty storage and flash an empty state for the user's own data.
  if (!hydrated) {
    return <div className="cg-shell" />;
  }

  if (view === "scorecard") {
    if (!activeRound) {
      // Defensive — if scorecard view is somehow set without a round
      // (e.g. resumed then cleared in another tab), bounce home.
      setView("landing");
      return null;
    }
    // Tournament games (bets, entry-fee-paid, etc.) are owned by the admin, so
    // read them LIVE from the loaded tournament rather than the snapshot taken
    // when the player tapped in. Fall back to the snapshot if the tournament is
    // no longer in the player's list.
    const liveTournamentGames =
      liveTournament?.games ?? activeRound.tournamentGames;
    return (
      <div className="cg-page">
        <Scorecard
          course={activeRound.course}
          players={activeRound.players}
          scores={activeRound.scores}
          displayTeeId={activeRound.displayTeeId}
          games={activeRound.games}
          ooslerSelections={activeRound.ooslerSelections}
          wolfSelections={activeRound.wolfSelections}
          bingoSelections={activeRound.bingoSelections}
          onPlayersChange={setPlayers}
          onScoresChange={setScores}
          onDisplayTeeChange={setDisplayTeeId}
          onGamesChange={setGames}
          onOoslerSelectionsChange={setOoslerSelections}
          onWolfSelectionsChange={setWolfSelections}
          onBingoSelectionsChange={setBingoSelections}
          onChangeCourse={() => setView("landing")}
          onEndRound={endRound}
          lockRoster={!!activeRound.tournamentId}
          tournamentGames={liveTournamentGames}
          tournamentId={activeRound.tournamentId}
          tournamentGroupId={activeGroupId}
          tournamentGroupName={activeGroupName}
        />
      </div>
    );
  }

  if (view === "select-course") {
    // Tournaments the player is in (any date, minus finished groups). Tapping
    // TODAY's bypasses course/player selection and jumps straight into the
    // pre-built group; tapping a future one opens the read-only draw (groups +
    // tee times) — starting is only allowed on the day.
    const today = todayLocalISO();
    const visibleTournaments = myTournaments
      .filter((t) => !t.groupFinal)
      .sort((a, b) => a.date.localeCompare(b.date));
    return (
      <div className="cg-page cg-page-fill">
        {visibleTournaments.length > 0 && (
          <div className="cg-tourney-panel">
            <div className="cg-tourney-panel-label">Tournaments</div>
            {visibleTournaments.map((t) => {
              const isToday = t.date === today;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`cg-tourney-btn${
                    isToday ? " cg-tourney-btn-today" : ""
                  }`}
                  onClick={() =>
                    isToday ? startTournamentRound(t) : setDrawTournament(t)
                  }
                >
                  <span className="cg-tourney-btn-name">
                    {t.tournamentName}
                  </span>
                  <span className="cg-tourney-btn-meta">
                    {isToday
                      ? "Today — tap to start"
                      : formatTournamentDate(t.date)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {favMsg && <div className="cg-fav-msg">{favMsg}</div>}
        <CourseSelect
          courses={favoriteCourses}
          serverSearch={playerCourseSearch}
          loadFacets={playerLoadFacets}
          onSelect={startNewRound}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
        />
        <button
          type="button"
          className="cg-btn cg-btn-ghost cg-back-btn"
          onClick={() => setView("landing")}
        >
          ← Back
        </button>
        {drawTournament && (
          <TournamentDrawModal
            tournament={drawTournament}
            onClose={() => setDrawTournament(null)}
          />
        )}
      </div>
    );
  }

  if (view === "players") {
    return (
      <div className="cg-page">
        <Players onBack={() => setView("landing")} />
      </div>
    );
  }

  if (view === "history") {
    return (
      <div className="cg-page cg-page-fill">
        <History onBack={() => setView("landing")} />
      </div>
    );
  }

  return (
    <div className="cg-page cg-page-landing">
      <Landing
        hasActiveRound={!!activeRound}
        onPlay={() => setView("select-course")}
        onResume={resumeRound}
        onPlayers={() => setView("players")}
        onHistory={() => setView("history")}
      />
    </div>
  );
}
